import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOrganization } from '../../../context/OrganizationContext';
import { getGA4Status, initiateGA4OAuth, disconnectGA4, updateGA4Property, getOverviewReport } from '../../../services/externalService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import toast from 'react-hot-toast';

const GA4 = ({ organizationData, organizationId }) => {
  const location = useLocation();
  const { userRole } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [showPropertySelector, setShowPropertySelector] = useState(false);
  const [showTestSection, setShowTestSection] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [showReportDrawer, setShowReportDrawer] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState(null);

  // Determine permissions based on role
  const isAdmin = userRole === 'owner' || userRole === 'admin';
  const isAnalyst = userRole === 'analyst';
  const isViewer = userRole === 'viewer';
  
  // Permission flags
  const canConnectDisconnect = isAdmin;
  const canChangeProperty = isAdmin || isAnalyst;

  // Load GA4 integration status on component mount
  useEffect(() => {
    if (organizationId) {
      loadGA4Status();
    }
  }, [organizationId]);

  // Refresh status when returning from OAuth (detect URL changes)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('ga4_success') === 'true') {
      // Small delay to ensure the backend has saved the data
      setTimeout(() => {
        loadGA4Status();
      }, 1000);
    }
  }, [location.search]);

  const loadGA4Status = async () => {
    try {
      const response = await getGA4Status(organizationId);
      const data = response.data;
      setIsConnected(data.connected);
      if (data.connected) {
        setConnectionInfo(data);
      }
    } catch (error) {
      // Error loading GA4 status
      setIsConnected(false);
      setConnectionInfo(null);
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    try {
      const response = await initiateGA4OAuth(organizationId);
      const data = response.data;
      // Redirect to Google OAuth authorization URL
      window.location.href = data.authUrl;
    } catch (error) {
      alert('Failed to connect GA4');
      const errorMessage = error.response?.data?.message || error.message || 'Failed to initiate GA4 connection';
      toast.error(errorMessage, {
        duration: 4000,
        position: 'top-right',
      });
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Analytics? This will remove all stored tokens and settings.')) {
      return;
    }

    setLoading(true);
    try {
      await disconnectGA4(organizationId);
      setIsConnected(false);
      setConnectionInfo(null);
      setShowPropertySelector(false);
      
      toast.success('Google Analytics 4 disconnected successfully', {
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      alert('Failed to disconnect GA4');
      const errorMessage = error.response?.data?.message || error.message || 'Failed to disconnect Google Analytics 4';
      toast.error(errorMessage, {
        duration: 4000,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePropertyChange = async (propertyId, propertyName) => {
    setLoading(true);
    try {
      await updateGA4Property(organizationId, propertyId, propertyName);
      // Reload status to get updated info
      await loadGA4Status();
      setShowPropertySelector(false);
      
      toast.success(`GA4 property updated to: ${propertyName}`, {
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      alert('Failed to update GA4 property');
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update GA4 property';
      toast.error(errorMessage, {
        duration: 4000,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTestLoading(true);
    setTestResults(null);
    setReportError(null);
    
    try {
      // Fetch real analytics data
      const overviewReportResponse = await getOverviewReport(organizationId);
      
      setReportData(overviewReportResponse.data.data);
      
      setTestResults({
        success: true,
        message: 'Connection test successful!',
        details: 'Successfully fetched analytics data from your GA4 property'
      });
      
      // Open the drawer to show the report
      setShowReportDrawer(true);
      
      toast.success('GA4 connection verified successfully', {
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      alert('Test connection failed');
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch analytics data';
      setReportError(errorMessage);
      setTestResults({
        success: false,
        message: 'Connection test failed',
        details: errorMessage
      });
      
      toast.error(errorMessage, {
        duration: 4000,
        position: 'top-right',
      });
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Connection Status Header - Only show when connected */}
      {isConnected && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            {/* Google Analytics Icon */}
            <div className="w-14 h-14 bg-white rounded-lg shadow-sm flex items-center justify-center flex-shrink-0">
              <svg className="w-8 h-8 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-xl font-semibold text-gray-900">Google Analytics 4</h3>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  isConnected 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-gray-100 text-gray-700 border border-gray-200'
                }`}>
                  {isConnected ? '● Connected' : '○ Not Connected'}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                {isConnected 
                  ? 'Your Google Analytics 4 property is connected and ready to use'
                  : 'Connect your Google Analytics 4 property to enable analytics tracking and insights'
                }
              </p>

              {/* Quick Stats when connected */}
              {isConnected && connectionInfo && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Account</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{connectionInfo.email}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Connected Since</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(connectionInfo.connectedAt).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Current Property</p>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {connectionInfo.selectedPropertyName || 'Not selected'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 ml-4">
            {canConnectDisconnect && (
              <>
                {isConnected ? (
                  <>
                    <button
                      onClick={loadGA4Status}
                      disabled={loading}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                    <button
                      onClick={handleDisconnect}
                      disabled={loading}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleConnect}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    {loading ? 'Connecting...' : 'Connect GA4'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      )}
      {/* boilerplate */}

      {/* Property Management Section */}
      {isConnected && connectionInfo && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h4 className="text-base font-semibold text-gray-900">Property Configuration</h4>
            <p className="text-sm text-gray-600 mt-1">Manage your GA4 property selection and settings</p>
          </div>
          
          <div className="p-6">
            {connectionInfo.properties && connectionInfo.properties.length > 0 ? (
              <div className="space-y-4">
                {/* Current Property Display */}
                {connectionInfo.selectedPropertyId && connectionInfo.selectedPropertyName && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-blue-900 mb-1">ACTIVE PROPERTY</p>
                        <p className="text-base font-semibold text-blue-900">{connectionInfo.selectedPropertyName}</p>
                        <p className="text-sm text-blue-700 mt-1">Property ID: {connectionInfo.selectedPropertyId}</p>
                      </div>
                      {(canChangeProperty && connectionInfo.properties.length > 1) && (
                        <button
                          onClick={() => setShowPropertySelector(!showPropertySelector)}
                          disabled={loading}
                          className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {showPropertySelector ? 'Cancel' : 'Change'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Property Selector */}
                {showPropertySelector && canChangeProperty && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select a different GA4 property:
                    </label>
                    <div className="space-y-2">
                      {connectionInfo.properties.map((property) => (
                        <button
                          key={property.id}
                          onClick={() => handlePropertyChange(property.id, property.displayName)}
                          disabled={loading || property.id === connectionInfo.selectedPropertyId}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            property.id === connectionInfo.selectedPropertyId
                              ? 'border-blue-200 bg-blue-50 cursor-default'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{property.displayName}</p>
                              <p className="text-sm text-gray-500">ID: {property.id}</p>
                            </div>
                            {property.id === connectionInfo.selectedPropertyId && (
                              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* No property selected warning */}
                {!connectionInfo.selectedPropertyId && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-yellow-900">No property selected</p>
                        <p className="text-sm text-yellow-700 mt-1">Please select a GA4 property to start tracking analytics.</p>
                        {canChangeProperty && (
                          <button
                            onClick={() => setShowPropertySelector(true)}
                            className="mt-2 text-sm font-medium text-yellow-900 hover:text-yellow-800 underline"
                          >
                            Select Property
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-gray-600">No GA4 properties available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Connection Section */}
      {isConnected && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowTestSection(!showTestSection)}
            className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-left">
                <h4 className="text-base font-semibold text-gray-900">Test Connection</h4>
                <p className="text-sm text-gray-600">Verify your GA4 connection is working properly</p>
              </div>
            </div>
            <svg 
              className={`w-5 h-5 text-gray-500 transition-transform ${showTestSection ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showTestSection && (
            <div className="p-6 border-t border-gray-200">
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Run a test to verify your GA4 connection and view live analytics data from your property.
                </p>
                
                <button
                  onClick={handleTestConnection}
                  disabled={testLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {testLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Fetching Analytics Data...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Test Connection
                    </>
                  )}
                </button>

                {/* Test Results */}
                {testResults && (
                  <div className={`mt-4 p-4 rounded-lg border ${
                    testResults.success 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      {testResults.success ? (
                        <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                      <div>
                        <p className={`text-sm font-medium ${
                          testResults.success ? 'text-green-900' : 'text-red-900'
                        }`}>
                          {testResults.message}
                        </p>
                        <p className={`text-sm mt-1 ${
                          testResults.success ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {testResults.details}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Not Connected State - Call to Action */}
      {!isConnected && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect Your Google Analytics 4</h3>
            <p className="text-gray-600 mb-6">
              Start tracking your website's performance by connecting your GA4 property. You'll be able to access analytics data, run reports, and gain insights into your audience.
            </p>
            {canConnectDisconnect && (
              <button
                onClick={handleConnect}
                disabled={loading}
                className="px-6 py-3 text-base font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {loading ? 'Connecting...' : 'Connect Google Analytics 4'}
              </button>
            )}
            {!canConnectDisconnect && (
              <p className="text-sm text-gray-500">Contact an administrator to connect GA4</p>
            )}
          </div>
        </div>
      )}

      {/* Report Drawer */}
      {showReportDrawer && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowReportDrawer(false)}
          ></div>
          
          {/* Drawer */}
          <div className="absolute inset-y-0 right-0 max-w-4xl w-full bg-white shadow-xl flex flex-col animate-slide-in-right">
            {/* Drawer Header - Success Banner with Close Button */}
            <div className="px-6 py-4 bg-green-50 border-b border-green-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-green-900">Connection Test Successful</p>
                  <p className="text-sm text-green-700">Successfully retrieved analytics data from your GA4 property</p>
                </div>
              </div>
              <button
                onClick={() => setShowReportDrawer(false)}
                className="text-green-700 hover:bg-green-100 rounded-lg p-2 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Drawer Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {reportData ? (
                <div className="space-y-6">
                  {/* Analytics Overview */}
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-white">Analytics Overview</h3>
                          <p className="text-blue-100 text-sm mt-1">
                            {reportData.property?.name} • {reportData.dateRange}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {[
                          { label: 'Active Users', value: reportData.summary?.activeUsers || 0, color: 'text-blue-600', bg: 'bg-blue-50' },
                          { label: 'New Users', value: reportData.summary?.newUsers || 0, color: 'text-green-600', bg: 'bg-green-50' },
                          { label: 'Sessions', value: reportData.summary?.sessions || 0, color: 'text-purple-600', bg: 'bg-purple-50' },
                          { label: 'Page Views', value: reportData.summary?.screenPageViews || 0, color: 'text-orange-600', bg: 'bg-orange-50' },
                          { label: 'Bounce Rate', value: `${Math.round((reportData.summary?.bounceRate || 0) * 100)}%`, color: 'text-red-600', bg: 'bg-red-50' },
                          { label: 'Avg. Session', value: `${Math.round(reportData.summary?.averageSessionDuration || 0)}s`, color: 'text-indigo-600', bg: 'bg-indigo-50' }
                        ].map((stat, index) => (
                          <div key={index} className={`${stat.bg} rounded-lg p-4 text-center`}>
                            <div className={`text-2xl font-bold ${stat.color} mb-1`}>
                              {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                            </div>
                            <div className="text-xs text-gray-600 font-medium">
                              {stat.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Traffic Sources & Device Breakdown */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Traffic Sources */}
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                        Traffic Sources
                      </h3>
                      <div className="space-y-3">
                        {reportData.reports?.trafficSources?.slice(0, 5).map((source, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-blue-600 rounded-full mr-3"></div>
                              <span className="font-medium text-gray-900 text-sm">{source.sessionSource}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-gray-900">{source.sessions}</div>
                              <div className="text-xs text-gray-600">{source.activeUsers} users</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Device Breakdown Chart */}
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17 1H7c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zM7 4V3h10v1H7zM7 18V6h10v12H7z"/>
                        </svg>
                        Device Breakdown
                      </h3>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={reportData.reports?.deviceBreakdown?.map(device => ({
                              name: device.deviceCategory,
                              value: device.sessions,
                              users: device.activeUsers
                            })) || []}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                            label={({name, value}) => `${name}: ${value}`}
                          >
                            {reportData.reports?.deviceBreakdown?.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={['#3B82F6', '#6366F1', '#8B5CF6'][index % 3]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name, props) => [`${value} sessions`, `${props.payload.users} users`]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Top Pages */}
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                      </svg>
                      Top Pages
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={reportData.reports?.topPages?.slice(0, 10).map(page => ({
                          page: page.pagePath.length > 25 ? '...' + page.pagePath.slice(-25) : page.pagePath,
                          fullPath: page.pagePath,
                          views: page.screenPageViews,
                          users: page.activeUsers
                        })) || []}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="page" 
                          tick={{ fontSize: 11 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip 
                          formatter={(value, name) => [value, name === 'views' ? 'Page Views' : 'Active Users']}
                          labelFormatter={(label, payload) => {
                            const item = payload?.[0]?.payload;
                            return item?.fullPath || label;
                          }}
                        />
                        <Bar dataKey="views" fill="#3B82F6" name="views" />
                        <Bar dataKey="users" fill="#6366F1" name="users" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading report data...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GA4;
